import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, type Ref, ref, watch } from 'vue'
import type { CaseDTO, HospitalDTO } from '@/generated'
import { CaseService } from '@/services/telehealthApi/CaseService'
import { arrayToRecord } from '@/util/convertArrayToRecord'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import type { PatientFilter } from '@/stores/models/PatientFilter'
import { remove } from 'lodash'
import { useLocalStorage } from '@vueuse/core'
import { useUserStore } from './user'

const CASES_LIMIT = 25

export const useCaseStore = defineStore('case', () => {
  const casesLoading = ref(false)
  const nextCasesLoading = ref(false)
  const currentLimit = ref(CASES_LIMIT)

  const cases: Ref<Record<string, CaseDTO>> = ref({})

  const patientFilter: Ref<PatientFilter> = useLocalStorage('patientFilter', {
    hospitals: [],
    departments: [],
    wards: []
  })

  const patientFilterInput: Ref<PatientFilter> = ref(patientFilter.value)

  const caseList = computed<CaseDTO[]>(() => Object.values(cases.value))

  const hasMoreCases = computed<boolean>(() => {
    return totalCases.value > caseList.value.length
  })

  const totalCases = ref(0)

  async function refreshCases() {
    const { canListPatients } = storeToRefs(useUserStore())

    if (!canListPatients.value) {
      return
    }

    try {
      casesLoading.value = true
      const initialCasesResponse = await CaseService.getCases({ limit: 1 })

      if (initialCasesResponse.total === 0) {
        cases.value = {}
        totalCases.value = 0
        return
      }

      const casesResponse = await CaseService.getCases({
        limit: initialCasesResponse.total ?? CASES_LIMIT
      })
      cases.value = arrayToRecord(casesResponse.items)
      totalCases.value = casesResponse.total
    } catch (error) {
      cases.value = {}
      useNoticeStore().add({ type: NoticeType.ERROR, message: 'Failed to load patient data' })
    } finally {
      casesLoading.value = false
    }
  }

  function getCaseById(id: string): CaseDTO | undefined {
    return cases.value[id]
  }

  function commitPatientFilterInput() {
    patientFilter.value = { ...patientFilterInput.value }
  }

  function resetPatientFilterInput() {
    patientFilterInput.value = { ...patientFilter.value }
  }

  function cleanupPatientFilter() {
    cleanupFilter(patientFilter.value)
  }

  function cleanupPatientFilterInput() {
    cleanupFilter(patientFilterInput.value)
  }

  function cleanupFilter(filter: PatientFilter) {
    const caseListFilteredByHospitals = caseList.value.filter((caze) =>
      filter.hospitals.some((hospital) => hospital.id === caze.hospital.id)
    )

    filter.wards = filter.wards.filter((wardName) => {
      return caseListFilteredByHospitals.some((caze) => caze.ward.name === wardName)
    })

    filter.departments = filter.departments.filter((departmentName) => {
      return caseListFilteredByHospitals.some((caze) => caze.department.name === departmentName)
    })
  }

  function removeHospitalFilter(hospitalId: string) {
    remove(patientFilter.value.hospitals, (h: HospitalDTO) => h.id === hospitalId)
    cleanupPatientFilter()
  }

  function removeDepartmentFilter(department: string) {
    remove(patientFilter.value.departments, (d: string) => d === department)
  }

  function removeWardFilter(ward: string) {
    remove(patientFilter.value.wards, (w: string) => w === ward)
  }

  async function loadMoreCases() {
    if (!hasMoreCases.value) {
      return
    }

    nextCasesLoading.value = true

    try {
      currentLimit.value += CASES_LIMIT
      const currentNumberOfCases = Object.keys(cases.value).length

      const casesResponse = await CaseService.getCases({
        offset: currentNumberOfCases,
        limit: currentLimit.value
      })

      cases.value = {
        ...cases.value,
        ...arrayToRecord(casesResponse.items)
      }
      totalCases.value = casesResponse.total
    } catch (e) {
      cases.value = {}
      useNoticeStore().add({ type: NoticeType.ERROR, message: 'Failed to load more patient data' })
    } finally {
      nextCasesLoading.value = false
    }
  }

  watch(
    () => patientFilterInput.value.hospitals,
    () => {
      cleanupPatientFilterInput()
    }
  )

  return {
    nextCasesLoading,
    casesLoading,
    cases,
    caseList,
    patientFilter,
    patientFilterInput,
    commitPatientFilterInput,
    resetPatientFilterInput,
    refreshCases,
    getCaseById,
    removeHospitalFilter,
    removeDepartmentFilter,
    removeWardFilter,
    loadMoreCases
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCaseStore, import.meta.hot))
}
